import {cloneDeep, get, groupBy, head} from "lodash"
import {Auth} from "types/auth"
import {DefaultDataType, DigitalDocumentFieldSetUp, DigitalTaskCondition} from "types/tasks"
import {useDigitalSetup} from "./useDigitalSetup"

export const useSubmit = () => {
    const {getScaleInfo, scalePlaceInformation} = useDigitalSetup()

    const getDependencyFieldValue = ({dependencyField}) => {
        if (dependencyField.condition.id === DigitalTaskCondition.FILLED_OUT) {
            return true
        }
        return dependencyField.value.id
    }

    const getDataSubmit = ({fields, taskData, margin, pageRef, participants}) => {
        const {scaleHeight, scaleWidth, pageHeight} = getScaleInfo({pageRef, taskData})
        const fieldValuesGroupByParentId = groupBy(fields, "parentId")
        const tempFields = cloneDeep(fields)
        const fieldsData = tempFields
            .filter((field) => !field.parentId)
            .map((field, index) => {
                const {top} = field.placeInformation
                const page = Math.ceil((top + margin) / (pageHeight + margin))
                const newTop = top - (page - 1) * (pageHeight + margin)
                field.placeInformation.top = newTop
                field.page = page
                const item: any = {
                    dDocId: taskData.digitalDocument.id,
                    fieldOrder: index + 1,
                    label: field.label || "",
                    page,
                    required: field.required ? 1 : 0,
                    signatureType: "",
                    signerIds: field.signatureTypes
                        ? (field.signatureTypes || []).map((signatureType) => signatureType.id)
                        : field.signerIds,
                    type: field.type,
                    placeInformation: [scalePlaceInformation(field.placeInformation, {scaleHeight, scaleWidth})],
                    uuid: field.uuid,
                    dependencies: (field.dependencies || [])
                        .filter((dependencyField) => dependencyField.uuid?.uuid)
                        .map((dependencyField) => ({
                            uuid: dependencyField.uuid.uuid,
                            value: getDependencyFieldValue({dependencyField})
                        }))
                }
                switch (field.type) {
                    case DigitalDocumentFieldSetUp.SIGNATURE: {
                        item.signatureMethod = field.signatureMethod?.id
                        const dateField = fields.find(
                            (item) => item.parentId === field.id && item.type === DigitalDocumentFieldSetUp.DATE_FIELD
                        )
                        if (dateField) {
                            const {top} = dateField.placeInformation
                            const newTop = top - (page - 1) * (pageHeight + margin)
                            dateField.placeInformation.top = newTop
                            item.placeInformation = JSON.stringify([
                                {
                                    ...item.placeInformation[0],
                                    dateField: scalePlaceInformation(dateField.placeInformation, {
                                        scaleHeight,
                                        scaleWidth
                                    })
                                }
                            ])
                        } else {
                            item.placeInformation = JSON.stringify(item.placeInformation)
                        }
                        break
                    }
                    case DigitalDocumentFieldSetUp.INITIAL:
                    case DigitalDocumentFieldSetUp.CHECKBOX: {
                        const fieldValues = (fieldValuesGroupByParentId[field.id] || []).map((childField) => {
                            const {top} = childField.placeInformation
                            const childFieldPage = Math.ceil((top + margin) / (pageHeight + margin))
                            const newTop = top - (childFieldPage - 1) * (pageHeight + margin)
                            childField.page = childFieldPage
                            childField.placeInformation.top = newTop
                            return childField
                        })
                        fieldValues.unshift({...field, id: get(head(field.fieldValues), "id")})
                        item.fieldValues = fieldValues.map((fieldValue, index) => {
                            return {
                                id: fieldValue.id,
                                index: index + 1,
                                label: get(field.fieldValues, [index, "label"], ""),
                                page: fieldValue.page,
                                position: scalePlaceInformation(fieldValue.placeInformation, {
                                    scaleHeight,
                                    scaleWidth
                                })
                            }
                        })
                        item.fieldValues = JSON.stringify(item.fieldValues)
                        if (field.type === DigitalDocumentFieldSetUp.INITIAL) {
                            item.initialType = get(field.initialType, "id", "")
                        } else if (field.type === DigitalDocumentFieldSetUp.CHECKBOX) {
                            item.checkboxType = get(field.checkboxType, "id", "")
                        }
                        item.placeInformation = JSON.stringify(item.placeInformation)
                        break
                    }
                    case DigitalDocumentFieldSetUp.SELECT: {
                        const fieldValues = field.fieldValues.map((fieldValue, index) => {
                            return {
                                id: fieldValue.id,
                                index: index + 1,
                                option: fieldValue.option
                            }
                        })
                        item.fieldValues = JSON.stringify(fieldValues)
                        item.placeInformation = JSON.stringify(item.placeInformation)
                        break
                    }
                    case DigitalDocumentFieldSetUp.TEXT:
                        item.dataType = get(field, "dataType.id", "")
                        item.defaultDataType = get(field, "defaultDataType.id", "")
                        item.defaultParticipant = get(field, "defaultParticipant.id", "") || null
                        if (item.defaultDataType === DefaultDataType.MANUAL_TEXT) {
                            item.defaultData = field.defaultData || ""
                        } else if (item.defaultDataType === DefaultDataType.STUDENT_DATA) {
                            item.defaultData = get(field.defaultData, "id", "")
                            item.syncToEdular = field.syncToEdular
                        }
                        item.placeInformation = JSON.stringify(item.placeInformation)
                        break
                    case DigitalDocumentFieldSetUp.GRADE:
                        item.dataType = get(field, "dataType.id", "")
                        item.defaultData = DigitalDocumentFieldSetUp.GRADE
                        item.defaultParticipant = participants.find(
                            (item) => item.type === Auth.UserProfileType.Student
                        )?.id
                        item.placeInformation = JSON.stringify(item.placeInformation)
                        item.syncToEdular = true
                        break
                    case DigitalDocumentFieldSetUp.FILE:
                        item.fileType = get(field, "fileType.id", "")
                        item.placeInformation = JSON.stringify(item.placeInformation)
                        break
                    default:
                        break
                }

                return item
            })

        return {taskId: taskData.id, fields: fieldsData}
    }

    return {getDataSubmit}
}
